import { useEffect, useMemo, useState } from "react";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import moment from "moment";
import { gridSx, useNewLeadsContext } from "../NewLeads";
import { getAssignedLeads } from "../../../services/newLeads";

const PAGE_SIZE = 10;

const FIELD_LABELS: Record<string, string> = {
  type: "Type",
  domainTitle: "Domain",
  isEmergency: "Emergency",
  preferredDays: "Preferred Days",
  preferredTime: "Preferred Time",
  notes: "Notes",
  status: "Status",
  createdAt: "Created At",
  name: "Clinic Name",
  firstName: "First Name",
  lastName: "Last Name",
  phone: "Phone",
  email: "Email",
  city: "City",
  zipcode: "Zipcode",
  formattedAddress: "Address",
};

const FIELD_ORDER = [
  "name",
  "type",
  "domainTitle",
  "isEmergency",
  "preferredDays",
  "preferredTime",
  "notes",
  "createdAt",
  "name",
  "firstName",
  "lastName",
  "phone",
  "email",
  "city",
  "zipcode",
  "formattedAddress",
];

function formatFieldValue(key: string, val: unknown): React.ReactNode {
  if (val === null || val === undefined || val === "") return "--";
  if (key === "createdAt" && val)
    return moment(String(val)).format("MMM D, YYYY • h:mm a");
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 13,
        color: "#111827",
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value ?? "--"}
    </span>
  </div>
);

const AssignedLeadDetailDialog = ({
  lead,
  onClose,
}: {
  lead: Record<string, unknown> | null;
  onClose: () => void;
}) => {
  const entries = useMemo(() => {
    if (!lead) return [];
    const seen = new Set<string>();
    const rows: { key: string; label: string; value: React.ReactNode }[] = [];
    for (const key of FIELD_ORDER) {
      if (Object.prototype.hasOwnProperty.call(lead, key)) {
        seen.add(key);
        rows.push({
          key,
          label: FIELD_LABELS[key] || key,
          value: formatFieldValue(key, lead[key]),
        });
      }
    }
    return rows;
  }, [lead]);

  const patientName =
    lead &&
    `${String(lead.firstName ?? "")} ${String(lead.lastName ?? "")}`.trim();

  return (
    <Dialog
      open={!!lead}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ style: { borderRadius: 10 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          fontSize: 16,
          fontWeight: 600,
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "relative",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {(patientName || "?").charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {patientName || "Lead details"}
          </div>
          {lead != null &&
            lead.createdAt != null &&
            String(lead.createdAt) !== "" && (
              <div style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>
                {moment(String(lead.createdAt)).format("MMM D, YYYY • h:mm a")}
              </div>
            )}
        </div>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "14px 24px",
            background: "#f9fafb",
            borderRadius: 10,
            padding: 16,
          }}
        >
          {entries.map(({ key, label, value }) => (
            <div
              key={key}
              style={
                key === "formattedAddress" || key === "notes"
                  ? { gridColumn: "1 / -1" }
                  : undefined
              }
            >
              <DetailRow label={label} value={value} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Assigned = () => {
  const { selectedClinicIds, startDate, endDate } = useNewLeadsContext();

  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const primaryClinicId = selectedClinicIds[0];

  useEffect(() => {
    setLoading(true);
    getAssignedLeads({
      page: paginationModel.page + 1,
      record: paginationModel.pageSize,
      clinicId: primaryClinicId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    })
      .then((res: any) => {
        const data = res?.data || res || {};
        const leads = data.leads || data.data || [];
        setRows(leads);
        setTotalRows(data.total ?? leads.length ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    primaryClinicId,
    startDate,
    endDate,
  ]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Clinic",
      minWidth: 220,
      renderCell: (params) => (
        <span style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>
          {params.row.clinicName || params.value || "--"}
        </span>
      ),
    },
    {
      field: "patientName",
      headerName: "Patient",
      minWidth: 250,
      valueGetter: (_v, row) =>
        row.patientName ||
        `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
        "--",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
            {params.value as string}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.phone}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.email}
          </div>
        </div>
      ),
    },
    {
      field: "cityPostal",
      headerName: "City / Postal",
      minWidth: 270,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 400, fontSize: 14, color: "#111827" }}>
            {params.row.city || "--"}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 12 }}>
            {params.row.zipcode || ""}
          </div>
          {params.row.formattedAddress && (
            <div style={{ color: "#6b7280", fontSize: 11 }}>
              {params.row.formattedAddress}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "Assigned At",
      headerName: "Assigned At",
      minWidth: 170,
      renderCell: (params) => (
        <span style={{ color: "#374151", fontSize: 13, whiteSpace: "nowrap" }}>
          {params.row.assignedDate
            ? moment(params.row.assignedDate).format("MMM D, YYYY • h:mm a")
            : "--"}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 170,
      renderCell: (params) => (
        <span style={{ color: "#374151", fontSize: 13, whiteSpace: "nowrap" }}>
          {params.row.createdAt
            ? moment(params.row.createdAt).format("MMM D, YYYY • h:mm a")
            : "--"}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 128,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLead(params.row as Record<string, unknown>);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            border: "1px solid #d1d5db",
            borderRadius: 7,
            background: "#fff",
            cursor: "pointer",
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            whiteSpace: "nowrap",
            height: 30,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#f3f4f6";
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "#9ca3af";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#fff";
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "#d1d5db";
          }}
        >
          View Details
        </button>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          margin: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: "#111827" }}>
              Assigned Leads
            </span>
            <span style={{ color: "#9ca3af", fontSize: 13 }}>
              {totalRows} total
            </span>
          </div>
        </div>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id ?? row.requestId ?? row.leadId}
          autoHeight
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          density="standard"
          sx={gridSx}
        />
      </div>

      <AssignedLeadDetailDialog
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
      />
    </>
  );
};
